<template>
  <div class="slider-container">
    <div class="slider">
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="slider-slide"
        :style="{
          backgroundImage: `url(${getImagePath(slide.background_image)})`,
        }"
        :class="{ active: index === current_index }"
      >
        <div class="slider-overlay" :class="{ 'no-overlay': !slide.title }">
          <div class="slider-content" :class="{ 'no-title': !slide.title }">
            <h1 v-if="slide.title" class="slider-title">{{ slide.title }}</h1>
            <p v-if="slide.subtitle" class="slider-subtitle">
              {{ slide.subtitle }}
            </p>
            <button
              v-if="slide.button_text"
              class="slider-btn"
              :class="slide.button_style || 'primary'"
              :disabled="slide.button_disabled"
              @click="
                !slide.button_disabled && handleButtonClick(slide.button_link)
              "
            >
              {{ slide.button_text }}
            </button>
          </div>
        </div>
      </div>

      <button class="nav-btn prev" @click="prevSlide">
        <img src="@/assets/icons/papsi_arrow_back.svg" alt="Previous" />
      </button>
      <button class="nav-btn next" @click="nextSlide">
        <img src="@/assets/icons/papsi_arrow_forward.svg" alt="Next" />
      </button>

      <div class="pagination">
        <span
          v-for="(slide, index) in slides"
          :key="index"
          :class="['dot', { active: index === current_index }]"
          @click="goToSlide(index)"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
import mixin from "@/mixin";

export default {
  name: "SliderControl",
  mixins: [mixin],
  props: {
    slides: {
      type: Array,
      required: true,
      default: () => [
        {
          background_image: "",
          title: "",
          subtitle: "",
          button_text: "",
          button_link: "",
        },
      ],
    },
    auto_slide_interval: {
      type: Number,
      default: 5, // default 5 seconds
    },
    button_style: {
      type: String,
      default: "primary",
    },
  },
  data() {
    return {
      current_index: 0,
      auto_timer: null,
    };
  },
  mounted() {
    this.startAutoSlide();
  },
  beforeUnmount() {
    this.stopAutoSlide();
  },
  methods: {
    getImagePath(image_path) {
      if (!image_path) return "";
      // If the path is already a full URL
      if (image_path.startsWith("http") || image_path.startsWith("/"))
        return image_path;
      try {
        // Use require only for relative assets
        return require(`@/assets/${image_path}`);
      } catch (e) {
        console.warn(`Image not found: ${image_path}`, e);
        return ""; // fallback to empty so slide still renders but won't break
      }
    },
    handleButtonClick(link) {
      if (!link) return;
      // Ensure exact navigation
      if (link.startsWith("http")) {
        window.open(link, "_blank");
      } else if (link.startsWith("/")) {
        this.$router.push({ path: link });
      } else {
        this.$router.push({ name: link });
      }
    },
    nextSlide() {
      this.current_index = (this.current_index + 1) % this.slides.length;
      this.resetAutoSlide();
    },
    prevSlide() {
      this.current_index =
        (this.current_index - 1 + this.slides.length) % this.slides.length;
      this.resetAutoSlide();
    },
    goToSlide(index) {
      this.current_index = index;
      this.resetAutoSlide();
    },
    startAutoSlide() {
      if (this.auto_slide_interval <= 0) return;
      this.auto_timer = setInterval(() => {
        this.nextSlide();
      }, this.auto_slide_interval * 1000);
    },
    stopAutoSlide() {
      if (this.auto_timer) clearInterval(this.auto_timer);
      this.auto_timer = null;
    },
    resetAutoSlide() {
      this.stopAutoSlide();
      this.startAutoSlide();
    },
  },
};
</script>

<style scoped>
.slider-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slider {
  position: relative;
  width: 100%;
  height: 450px;
  border-radius: 10px;
  overflow: hidden;
}

.slider-slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transition: opacity 1s ease-in-out;
  pointer-events: none; /* Prevent clicks on inactive slides */
  z-index: 0; /* Ensure active slide is on top */
}

.slider-slide.active {
  opacity: 1;
  pointer-events: auto; /* Enable clicks */
  z-index: 1;
}

.slider-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.slider-overlay.no-overlay {
  background: transparent;
}

.slider-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  max-width: 900px; 
  width: 80%; 
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.slider-content.no-title {
  top: auto;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  flex-direction: column;
  justify-content: flex-end;
}

.slider-content.no-title .slider-btn {
  margin-top: 0;
}

.slider-title {
  font-size: 3rem;
  margin: 0 0 10px;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.8);
}

.slider-subtitle {
  font-size: 1.1rem;
  margin: 0 0 20px;
}

.slider-btn {
  padding: 10px 20px;
  background-color: #fbd603;
  border: none;
  border-radius: 5px;
  color: #016b00;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.slider-btn:hover {
  background-color: #016b00;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.nav-btn img {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-50%) scale(1.1);
}

.prev {
  left: 12px;
}

.next {
  right: 12px;
}

.pagination {
  position: absolute;
  bottom: 15px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.dot {
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.dot.active {
  background: white;
  transform: scale(1.2);
}

@media (max-width: 900px) {
  .slider {
    height: 40vh;
  }

  .slider-content {
    max-width: 90%;
    padding: 0 10px;
    text-align: center;
  }

  .slider-title {
    font-size: 1.5rem;
  }

  .slider-subtitle {
    font-size: 1rem;
  }

  .slider-btn {
    padding: 8px px;
    font-size: 0.9rem;
  }

  .nav-btn {
    font-size: 1.5rem;
  }
}

.slider-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc;
  color: #666;
  box-shadow: none;
}

.slider-btn.primary {
  background-color: #fbd603;
  color: #016b00;
}
.slider-btn.primary:hover {
  background-color: #016b00;
  color: white;
}

.slider-btn.secondary {
  background-color: #016b00;
  color: white;
}
.slider-btn.secondary:hover {
  background-color: #024f00;
  color: #fbd603;
}

.slider-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc;
  color: #666;
  box-shadow: none;
}
</style>
